// Fork: per-component 90-day uptime bars on the public status page.
import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { LEVEL_BAR_CLASS, LEVEL_ICON_CLASS, LEVEL_LABEL, useStatusHref, type StatusLevel } from './statusShared'

export type ComponentLevel = StatusLevel

export interface ComponentDay {
  date: string
  level: ComponentLevel
  incidents?: { id: string; title: string; severity: string; level: ComponentLevel }[]
}

export interface ComponentUptimeData {
  key: string
  name: string
  description?: string
  status: ComponentLevel
  uptime_pct: number
  days: ComponentDay[]
}

export interface StatusComponentsData {
  timezone: string
  window_days: number
  components: ComponentUptimeData[]
}

const MOBILE_DAYS = 30
const POPOVER_WIDTH = 288
// The popover may extend this far past the bars into the card's padding, so
// edge days sit closer to the popover's corner.
const POPOVER_OVERHANG = 12
// While a popover is open, crossing other bars on the way to its links must
// not switch days: switch only after the pointer rests on a bar this long.
const SWITCH_DELAY_MS = 150

const LEVEL_TEXT_CLASS: Record<ComponentLevel, string> = {
  operational: 'text-green-700',
  degraded: 'text-yellow-800',
  outage: 'text-red-700',
}

function formatDay(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

function formatUptime(pct: number): string {
  return `${pct.toFixed(pct === 100 ? 0 : 2)}% uptime`
}

function DayPopover({ day, left }: { day: ComponentDay; left: number }) {
  const href = useStatusHref()
  const incidents = day.incidents ?? []
  return (
    // pt-1 is a transparent bridge: the pointer moves from the bars straight into the card.
    <div data-testid="day-popover" className="absolute top-full z-20 pt-1" style={{ left, width: POPOVER_WIDTH }}>
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="text-sm text-gray-500 mb-2">{formatDay(day.date)}</p>
        {incidents.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-gray-700">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" aria-hidden="true" />
            No incidents
          </p>
        ) : (
          <ul className="space-y-1">
            {incidents.map(incident => (
              <li key={incident.id}>
                <Link
                  to={href(`/incidents/${incident.id}`)}
                  className="flex items-start gap-2 rounded-md p-1.5 -mx-1.5 text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                >
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${LEVEL_ICON_CLASS[incident.level]}`} aria-label={LEVEL_LABEL[incident.level]} />
                  <span className="break-words">{incident.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function DayBars({ component, windowDays }: { component: ComponentUptimeData; windowDays: number }) {
  const [active, setActive] = useState<{ index: number; left: number } | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const barRefs = useRef<(HTMLSpanElement | null)[]>([])
  const switchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const days = component.days

  useEffect(() => () => clearTimeout(switchTimer.current), [])

  function open(index: number) {
    clearTimeout(switchTimer.current)
    const bar = barRefs.current[index]
    const wrapper = wrapperRef.current
    let left = 0
    if (bar && wrapper) {
      const center = bar.offsetLeft + bar.offsetWidth / 2
      left = Math.max(-POPOVER_OVERHANG, Math.min(center - POPOVER_WIDTH / 2, wrapper.clientWidth - POPOVER_WIDTH + POPOVER_OVERHANG))
    }
    setActive({ index, left })
  }

  function close() {
    clearTimeout(switchTimer.current)
    setActive(null)
  }

  function hover(index: number) {
    if (!active) {
      open(index)
      return
    }
    clearTimeout(switchTimer.current)
    if (active.index !== index) switchTimer.current = setTimeout(() => open(index), SWITCH_DELAY_MS)
  }

  function firstVisibleIndex(): number {
    // On narrow screens only the last MOBILE_DAYS bars are displayed.
    const index = barRefs.current.findIndex(bar => bar !== null && bar.offsetWidth > 0)
    return index < 0 ? 0 : index
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const current = active?.index ?? days.length - 1
    const moves: Record<string, number> = {
      ArrowLeft: Math.max(firstVisibleIndex(), current - 1),
      ArrowRight: Math.min(days.length - 1, current + 1),
      Home: firstVisibleIndex(),
      End: days.length - 1,
    }
    const target = moves[event.key]
    if (target !== undefined) {
      event.preventDefault()
      open(target)
    } else if (event.key === 'Escape') {
      close()
    }
  }

  function onBlur(event: FocusEvent<HTMLDivElement>) {
    if (!wrapperRef.current?.contains(event.relatedTarget as Node | null)) close()
  }

  const incidentDays = days.filter(day => day.level !== 'operational').length
  const activeDay = active ? days[active.index] : undefined
  return (
    <div ref={wrapperRef} className="relative mt-3" onMouseLeave={close} onBlur={onBlur}>
      <div
        role="group"
        tabIndex={0}
        aria-label={`${component.name}: daily history. Use the arrow keys to read each day.`}
        aria-describedby={`uptime-summary-${component.key}`}
        onKeyDown={onKeyDown}
        onFocus={() => { if (!active) open(days.length - 1) }}
        className="flex gap-px h-8 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
      >
        {days.map((day, index) => (
          <span
            key={day.date}
            ref={element => { barRefs.current[index] = element }}
            data-level={day.level}
            onMouseEnter={() => hover(index)}
            onMouseLeave={() => clearTimeout(switchTimer.current)}
            onClick={() => open(index)}
            className={`flex-1 rounded-sm cursor-pointer ${LEVEL_BAR_CLASS[day.level]} ${active?.index === index ? 'opacity-70' : ''} ${index < days.length - MOBILE_DAYS ? 'hidden sm:block' : ''}`}
          />
        ))}
      </div>
      <p id={`uptime-summary-${component.key}`} className="sr-only">
        {incidentDays === 0
          ? `No incidents in the last ${windowDays} days.`
          : `Incidents on ${incidentDays} of the last ${windowDays} days.`}
      </p>
      <div aria-live="polite">
        {activeDay && active && <DayPopover day={activeDay} left={active.left} />}
      </div>
    </div>
  )
}

function ComponentRow({ component, windowDays }: { component: ComponentUptimeData; windowDays: number }) {
  return (
    <div className="py-4 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 break-words">{component.name}</p>
          {component.description && <p className="text-xs text-gray-500 mt-0.5 break-words">{component.description}</p>}
        </div>
        <span className={`text-sm font-medium flex-shrink-0 ${LEVEL_TEXT_CLASS[component.status]}`}>
          {component.status === 'outage' ? 'Major outage' : LEVEL_LABEL[component.status]}
        </span>
      </div>
      <DayBars component={component} windowDays={windowDays} />
      <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-gray-600">
        <span className="hidden sm:inline">{windowDays} days ago</span>
        <span className="sm:hidden">{MOBILE_DAYS} days ago</span>
        <span>{formatUptime(component.uptime_pct)} <span className="sr-only">over {windowDays} days</span></span>
        <span>Today</span>
      </div>
    </div>
  )
}

export function ComponentUptime({ data }: { data: StatusComponentsData }) {
  if (data.components.length === 0) return null
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
        Systems — Last {data.window_days} Days
      </h2>
      <div className="bg-white border border-gray-200 rounded-lg px-4">
        {data.components.map(component => (
          <ComponentRow key={component.key} component={component} windowDays={data.window_days} />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${LEVEL_BAR_CLASS.operational}`} />No incidents</span>
        <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${LEVEL_BAR_CLASS.degraded}`} />Degraded</span>
        <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${LEVEL_BAR_CLASS.outage}`} />Outage</span>
        <span>Days in {data.timezone}.</span>
      </div>
    </section>
  )
}
